import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { OperatorHeader } from './OperatorHeader';
import { OperatorBottomNav, OperatorTab } from './OperatorBottomNav';
import { InstallCoach } from '../../components/InstallCoach';
import { PushConsentBanner } from '../../components/PushConsentBanner';
import './operator-mobile.css';

interface OperatorMobileShellProps {
  children: React.ReactNode;
  loading?: boolean;
  sseStatus?: 'connected' | 'stale' | 'disconnected';
  tenantName?: string;
  badges?: Partial<Record<OperatorTab, number>>;
}

function resolveTab(pathname: string): OperatorTab {
  const p = pathname.toLowerCase();
  if (p.includes('/notifications/audit') || p.includes('/audit')) return 'audit';
  if (p.includes('/inbox') || p.includes('/actions')) return 'actions';
  if (p.includes('/chat')) return 'chat';
  return 'fleet';
}

export function OperatorMobileShell({
  children,
  loading,
  sseStatus,
  tenantName,
  badges,
}: OperatorMobileShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = useMemo(() => resolveTab(location.pathname), [location.pathname]);
  const [pushBannerVisible, setPushBannerVisible] = useState(false);

  const handleTabChange = useCallback(
    (_tab: OperatorTab, route: string) => {
      navigate(route);
    },
    [navigate],
  );

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'NAVIGATE' && typeof event.data?.url === 'string') {
        const url = event.data.url;
        if (url.startsWith('/')) navigate(url);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [navigate]);

  const shellClassName = `operator-shell${pushBannerVisible ? ' operator-shell--push-banner-visible' : ''}`;

  return (
    <div className={shellClassName} data-testid="operator-shell">
      <OperatorHeader sseStatus={sseStatus} tenantName={tenantName} />
      <div className="operator-shell-main" id="main" role="main" data-testid="operator-main">
        {loading ? <OperatorShellSkeleton /> : children}
      </div>
      <OperatorBottomNav
        activeTab={activeTab}
        onTabChange={handleTabChange}
        badges={badges}
      />
      <InstallCoach />
      <PushConsentBanner onVisibilityChange={setPushBannerVisible} />
    </div>
  );
}

function OperatorShellSkeleton() {
  return (
    <div data-testid="operator-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 0' }}>
      {/* Health ring skeleton */}
      <div className="operator-skeleton-shimmer" style={{
        width: 160, height: 160, borderRadius: '50%', margin: '0 auto',
        background: 'var(--card-border, rgba(255,255,255,0.08))',
      }} />
      {/* KPI pills skeleton */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        {[1, 2, 3].map(i => (
          <div key={i} className="operator-skeleton-shimmer" style={{
            width: 100, height: 40, borderRadius: 20,
            background: 'var(--card-border, rgba(255,255,255,0.08))',
            animationDelay: `${i * 150}ms`,
          }} />
        ))}
      </div>
      {/* Card skeletons */}
      {[1, 2].map(i => (
        <div key={i} className="operator-skeleton-shimmer" style={{
          height: 120, borderRadius: 20,
          background: 'var(--card-border, rgba(255,255,255,0.08))',
          animationDelay: `${i * 200}ms`,
        }} />
      ))}
    </div>
  );
}
